import { useEffect, useRef } from "react";
import { useAuthStore } from "../store/authStore";

/**
 * Subscribe to realtime events over WebSocket for the current tenant.
 * `onEvent(msg)` is called for every JSON message received.
 * The socket automatically reconnects with exponential backoff.
 */
export default function useTenantWebSocket(onEvent) {
  const { user } = useAuthStore();
  const wsRef = useRef(null);
  const backoffRef = useRef(1000);
  const cbRef = useRef(onEvent);
  cbRef.current = onEvent;

  useEffect(() => {
    if (!user || !user.tenant_id) return;
    const tenantId = user.tenant_id;
    const userId = user.id;
    let cancelled = false;
    let pingTimer = null;

    const connect = () => {
      if (cancelled) return;
      const token = localStorage.getItem("taskora_access_token");
      if (!token) return;
      const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
      const wsUrl =
        backendUrl.replace(/^http/, "ws") + `/api/ws?token=${encodeURIComponent(token)}`;
      let ws;
      try {
        ws = new WebSocket(wsUrl);
      } catch {
        return;
      }
      wsRef.current = ws;
      window.__taskoraWs = ws;
      ws.onopen = () => {
        backoffRef.current = 1000;
        pingTimer = setInterval(() => {
          try {
            ws.send(JSON.stringify({ event: "ping" }));
          } catch {
            /* ignore */
          }
        }, 30000);
      };
      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (cbRef.current) cbRef.current(msg);
        } catch {
          /* ignore non-JSON */
        }
      };
      ws.onclose = () => {
        if (pingTimer) clearInterval(pingTimer);
        if (cancelled) return;
        const wait = Math.min(backoffRef.current, 15000);
        backoffRef.current = Math.min(backoffRef.current * 2, 30000);
        setTimeout(connect, wait);
      };
      ws.onerror = () => {
        try {
          ws.close();
        } catch {
          /* ignore */
        }
      };
    };

    connect();
    return () => {
      cancelled = true;
      if (pingTimer) clearInterval(pingTimer);
      const ws = wsRef.current;
      if (ws) {
        try {
          ws.close();
        } catch {
          /* ignore */
        }
      }
    };
  }, [user?.tenant_id, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps
}
